import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  AddNewUser,
  CareerIcons,
  CertificateIcon,
  CoursesIcons,
  FormIcons,
  IndustryAlliesIcon,
  JourneyIcon,
  MentorsIcons,
  MomentsIcons,
  PaymentIcon,
  PrivacyIcon,
  ResponseIcons,
  RoleAndPermission,
  SingleUser,
  SkillshikhyaIconWhite,
  SuccessStoryIcon,
  TeamMembers,
  TermsIcon,
} from "@assets/icons";
import { Spinner } from "@components/ui/spinner";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { showToast } from "@utils/helper/index.helper";
import { usePostData } from "@utils/query/index.query";
import {
  Check,
  CheckCircle,
  Clock,
  FileText,
  HelpCircle,
  Home,
} from "lucide-react";
import type React from "react";
import { useNavigate } from "react-router";

interface NotificationItem {
  id: number;
  type: "maintenance" | "payment" | "lease";
  title: string;
  description: string;
  timestamp: string | Date;
  created_at?: string | Date | undefined | any;
  message?: string;
  is_read: boolean;
  highlightedText?: { text: string; className: string }[];
}
interface NotificationItemProps {
  notification: NotificationItem;
  notifications?: any;
  refetch: () => void;
}

const getIcon = ({ type }: { type: string }) => {
  switch (type) {
    case "maintenance":
      return <Home className="w-4 h-4" />;
    case "lease":
      return <FileText className="w-4 h-4" />;
    case "student":
      return <SingleUser />;
    case "mentor":
      return <MentorsIcons className="w-4 h-4" />;
    case "course":
      return <CoursesIcons className="w-4 h-4" />;
    case "certificate":
      return <CertificateIcon className="w-4 h-4" />;
    case "Industryallies":
      return <IndustryAlliesIcon className="w-4 h-4" />;
    case "successstory":
      return <SuccessStoryIcon className="w-4 h-4" />;
    case "moment":
      return <MomentsIcons className="w-4 h-4" />;
    case "journey":
      return <JourneyIcon className="w-4 h-4" />;
    case "payment":
      return <PaymentIcon className="w-4 h-4" />;
    case "privacy":
      return <PrivacyIcon className="w-4 h-4" />;
    case "response":
      return <ResponseIcons className="w-4 h-4" />;
    case "roleandpermission":
      return <RoleAndPermission className="w-4 h-4" />;
    case "team":
      return <TeamMembers className="w-4 h-4" />;
    case "terms":
      return <TermsIcon className="w-4 h-4" />;
    case "skillshikshya":
      return <SkillshikhyaIconWhite />;
    case "addnewuser":
      return <AddNewUser className="w-4 h-4" />;
    case "career":
      return <CareerIcons className="w-4 h-4" />;
    case "form":
      return <FormIcons className="w-4 h-4" />;
    case "faqs":
      return <HelpCircle className="w-4 h-4" />;
    default:
      return <FileText className="w-4 h-4" />;
  }
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  notifications = [],
  refetch,
}) => {
  const type = notification.title.split(" ")[0].toLowerCase();
  const { mutate } = usePostData();
  const markAsRead = (id: number) => {
    mutate(
      {
        endpoint: endpoint["notification-as-read"],
        data: { id },
        method: METHODS.POST,
      },
      {
        onSuccess: () => {
          refetch();

          showToast({
            title: "Notification marked as read",
            description: "Notification marked as read successfully.",
            type: "success",
          });
        },
      }
    );
  };
  const navigate = useNavigate();
  return (
    <div
      className={`p-4 border-b overflow-clip group border-gray-100 ${
        !notification.is_read ? "bg-green-50" : "bg-white"
      }`}
    >
      <div className="flex transition-all delay-150  ease-in-out  items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 text-white bg-[#1da077] rounded-full flex items-center justify-center">
          {getIcon({ type })}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              {!notification.is_read && (
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              )}
              <h3 className="text-sm font-medium text-gray-900">
                {notification.title}
              </h3>{" "}
              <div className="flex items-center gap-1 text-xs text-gray-900">
                <Clock className="w-3 h-3" />
                {new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }).format(new Date(notification.created_at))}
              </div>
            </div>

            {!notification.is_read && (
              <div
                onClick={() => markAsRead(notification.id)}
                className=" -translate-x-[-200px] transition-all  delay-200 ease-in-out cursor-pointer flex gap-2 items-center h-full w-fit text-[14px]    group-hover:translate-x-2  text-gray-900"
              >
                Mark as Read <CheckCircle size={14} />
              </div>
            )}
          </div>
          <div className="text-sm w-[90%]">{notification.message}</div>
        </div>
      </div>{" "}
    </div>
  );
};

interface NotificationsPanelProps {
  notifications?: NotificationItem[];
  className?: string;
  isPending: boolean;
  notificationData: any;
  isFetchingNextPage: boolean;
  ref: any;
  refetch: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  className = "",
  isPending,
  notificationData,
  isFetchingNextPage,
  refetch,
  ref,
}) => {
  const date = new Date();
  const { mutate } = usePostData();
  const readAllNotifications = () => {
    mutate(
      {
        endpoint: endpoint["mark-all-as-read"],
        data: {},
        method: METHODS.POST,
      },
      {
        onSuccess: () => {
          refetch();
          showToast({
            description: "All Notifications have been read",
            title: "Success",
            type: "success",
          });
        },
      }
    );
  };
  return (
    <Card className={`w-full py-0 pt-6 max-w-md ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between  px-6 border-b">
        <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            readAllNotifications();
          }}
          className="text-green-600 hover:text-green-700 p-0 h-auto"
        >
          <Check className="w-4 h-4 mr-1" />
          Mark all as read
        </Button>
      </CardHeader>
      <CardContent className="p-0 h-full ">
        <div className="px-2 pb-3  border-b">
          <h3 className="text-sm font-medium text-gray-700">
            Today{" "}
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(date)}
          </h3>
        </div>
        <div className="max-h-[300px] min-w-[400px] h-[300px] overflow-y-auto">
          {" "}
          {isPending ? (
            <div className="flex items-center justify-center h-full">
              <Spinner />
            </div>
          ) : (
            notificationData?.map((notification: any) => (
              <NotificationItem
                key={notification.id}
                refetch={refetch}
                notification={notification}
                // @ts-ignore
                notifications={notificationData?.data?.results}
              />
            ))
          )}
          {isFetchingNextPage && (
            <div className="flex items-center bg-green-50 justify-center  h-12">
              <Spinner />
            </div>
          )}
          <div className=" w-full h-2   bg-green-50" ref={ref} />
          {/* */}
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationsPanel;
